import React, { useLayoutEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { visualAuthority } from './visualAuthority';
import {
  contrastRatio,
  deriveTypographyForeground,
  SEMANTIC_TYPOGRAPHY_LOW_MIX,
  SEMANTIC_TYPOGRAPHY_MEDIUM_MIX,
  buildComponentSurfaceVariables,
  extractBackgroundColors,
  getAccessibleForeground,
  parseColorToRgb,
  resolveForegroundColor,
} from './contrastAwareThemeEngine';
import { getImmediateVisualBackground } from './contextAwareForegroundEngine';

const MIN_TEXT_CONTRAST = 4.5;
const MIN_ICON_CONTRAST = 3;
const CONTROL_OVERLAY_ALPHA = 0.62;
const LOCALIZED_BUTTON_FIELD_SELECTOR = '[data-og-localized-button-field="true"]';
export const ACCESSIBLE_CONTROL_SELECTOR = [
  '[data-og-accessible-control="true"]',
  'button',
  '.btn',
  '.MuiButton-root',
  '.MuiIconButton-root',
  '.MuiToggleButton-root',
  '.MuiButtonGroup-root .MuiButton-root',
  '.MuiChip-root',
  '.MuiBadge-badge',
  '[data-og-semantic-surface]',
  '.og-semantic-surface',
  '.alert',
  '.MuiAlert-root',
  '.badge',
  '[role="button"]',
  '[role="menuitem"]',
  '[role="tab"]',
  '.nav-link',
  '.dropdown-item',
  '.MuiMenuItem-root',
  '.MuiListItemButton-root',
].join(',');

const SELECTED_SURFACE_SELECTOR = [
  '.active',
  '.selected',
  '.Mui-selected',
  '.dropdown-item.active',
  '.nav-link.active',
  '.MuiListItemButton-root.Mui-selected',
  '.document-navigation-button.active',
  '.unprocessed-receipts-tab.active',
  '.MuiTab-root.Mui-selected',
  '.MuiMenuItem-root.Mui-selected',
  '.MuiAutocomplete-option[aria-selected="true"]',
  '.MuiPickersDay-root.Mui-selected',
  '.MuiToggleButton-root.Mui-selected',
  '.MuiChip-root.Mui-selected',
  '.MuiBadge-badge',
  '.MuiAccordionSummary-root.Mui-expanded',
  '.btn-check:checked + .btn',
  '[aria-current="page"]',
  '[aria-pressed="true"]',
  '[aria-selected="true"]:not(option)',
  '[data-og-selected="true"]',
  '[data-state="active"]',
  '[data-state="checked"]',
].join(',');

const SURFACE_SELECTOR = [
  SELECTED_SURFACE_SELECTOR,
  '[data-og-adaptive-surface="true"]',
  '[data-og-context-foreground="true"]',
  '.og-adaptive-surface',
  '.og-context-aware-foreground',
  '.storefront-inline-editable-value',
  '[data-og-inline-editable-value="true"]',
  '.badge',
  '.chip',
  '.tag',
  '[class*="badge" i]',
  '[class*="chip" i]',
  '[class*="pill" i]',
  '[class*="tag" i]',
  '[class*="label" i]',
  '[class*="card" i]',
  '[class*="button" i]',
  '[class*="widget" i]',
  '[class*="dashboard" i]',
  '[class*="carousel" i]',
  '[class*="hero" i]',
  '[class*="landing" i]',
  '[class*="storefront" i]',
  '[role="tab"]',
  '[role="menuitem"]',
  '[role="option"]',
  '[role="gridcell"]',
  '[role="cell"]',
  '[role="columnheader"]',
  '[role="rowheader"]',
  'button',
  '.btn',
  '.nav-link',
  '.dropdown-item',
  '.MuiTab-root',
  '.MuiTabs-root',
  '.MuiMenuItem-root',
  '.MuiListItem-root',
  '.MuiListItemButton-root',
  '.MuiAccordion-root',
  '.MuiAccordionSummary-root',
  '.MuiAccordionDetails-root',
  '.MuiDialog-paper',
  '.MuiPopover-paper',
  '.MuiMenu-paper',
  '.MuiDrawer-paper',
  '.MuiAlert-root',
  '.MuiTableCell-root',
  '.MuiTableRow-root',
  '.MuiDataGrid-cell',
  '.MuiDataGrid-columnHeader',
  '.MuiInputBase-root',
  '.MuiOutlinedInput-root',
  '.MuiButton-root',
  '.MuiChip-root',
  '.MuiBadge-badge',
  '.MuiCard-root',
  '.MuiPaper-root',
  '.modal-content',
  '.modal-header',
  '.modal-body',
  '.modal-footer',
  '.offcanvas',
  '.toast',
  '.popover',
  '.tooltip',
  '.dropdown-menu',
  '.dropdown-item',
  '.toolbar',
  '[role="dialog"]',
  '[role="alertdialog"]',
  '[role="toolbar"]',
  '[role="navigation"]',
  '[role="listbox"]',
  '[role="status"]',
  '[role="alert"]',
  'input',
  'textarea',
  'select',
].join(',');

const TEXT_SELECTOR = [
  'span',
  'strong',
  'small',
  'em',
  'b',
  'p',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'label',
  'legend',
  'dt',
  'dd',
  'figcaption',
  'time',
  'code',
  'li',
  'td',
  'th',
  'a',
  'button',
  '.MuiTypography-root',
  '.MuiButton-root',
  '.MuiChip-label',
  '.MuiBadge-badge',
  '.MuiCardHeader-title',
  '.MuiCardHeader-subheader',
  '.MuiListItemText-root',
  '.MuiListItemText-primary',
  '.MuiListItemText-secondary',
  '.MuiTableCell-root',
  '.MuiDataGrid-cell',
  '.MuiDataGrid-columnHeaderTitle',
  '.MuiAccordionSummary-content',
  '.MuiInputLabel-root',
  '.MuiFormLabel-root',
  '.MuiInputBase-input',
  '.storefront-inline-editable-value',
].join(',');

const ICON_SELECTOR = [
  'svg',
  '.MuiSvgIcon-root',
  '.MuiIcon-root',
  '.fa',
  '.fas',
  '.far',
  '.fab',
  'i[class*="fa-"]',
].join(',');

const BUTTON_TEXT_DESCENDANT_SELECTOR = [
  'span',
  'strong',
  'small',
  'em',
  'b',
  'p',
  'label',
  'a',
  '.MuiTypography-root',
  '.MuiButton-label',
  '.MuiChip-label',
  '.MuiListItemIcon-root',
  '.MuiListItemText-root',
  '.MuiListItemText-primary',
  '.MuiListItemText-secondary',
  ICON_SELECTOR,
].join(',');

const SKIP_SELECTOR = [
  '[data-og-foreground-skip="true"]',
  '[data-og-preserve-colors="true"]',
  '[data-sidenav-link="true"]',
  '[data-og-asset-visibility="off"]',
  '.recharts-surface',
  '[class*="chart" i]',
  'canvas',
  'img',
  'video',
].join(',');

const ENTERPRISE_WORKSPACE_SELECTOR = '[data-og-workspace-theme="enterprise"]';

const EXPLICIT_SEMANTIC_BOUNDARY_SELECTOR = [
  '[data-og-semantic-surface]',
  '[data-og-component-surface]',
  '[data-og-adaptive-surface="true"]',
  '[data-og-context-foreground="true"]',
  '.og-semantic-surface',
].join(',');

const STRUCTURAL_SURFACE_SELECTOR = [
  'main',
  'section',
  'article',
  'header',
  'footer',
  'aside',
  'div',
  'nav',
  'ol',
  'ul',
  'li',
  'table',
  'thead',
  'tbody',
  'tr',
  'td',
  'th',
  '[role="row"]',
  '[role="cell"]',
  '[role="gridcell"]',
  '[role="columnheader"]',
].join(',');

const isElement = (node) => node && node.nodeType === 1;

const isComponentOwnedControl = (element) => {
  if (!isElement(element)) return false;
  // A component may own interactive state without owning the typography of
  // every semantic surface below it. Only real controls participate here;
  // cards, sections and dialogs must still publish their local foregrounds.
  const interactiveSelector = 'button,.btn,.MuiButton-root,.MuiIconButton-root,.MuiToggleButton-root,[role="button"],[role="tab"],[role="menuitem"],[role="option"],.nav-link,.dropdown-item';
  const control = element.matches?.(interactiveSelector)
    ? element
    : element.closest?.(interactiveSelector);
  return Boolean(control?.closest?.('[data-og-control-state-authority="component"]'));
};

const isEnterpriseWorkspaceStructure = (element) => isElement(element)
  && Boolean(element.closest?.(ENTERPRISE_WORKSPACE_SELECTOR))
  && Boolean(element.matches?.(STRUCTURAL_SURFACE_SELECTOR))
  && !Boolean(element.matches?.(EXPLICIT_SEMANTIC_BOUNDARY_SELECTOR));

const parseComputedColor = (value = '') => parseColorToRgb(String(value || '').trim());

const isTransparent = (value = '') => {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw || raw === 'transparent') {
    return true;
  }
  if (raw.startsWith('rgba')) {
    const alpha = Number(raw.match(/,\s*([\d.]+)\s*\)$/)?.[1]);
    return Number.isFinite(alpha) && alpha <= 0.02;
  }
  if (raw.includes('/ 0') || raw.includes('/0')) {
    return true;
  }
  return !parseComputedColor(raw);
};

const rgbToCss = (rgb) => (rgb ? `rgb(${Math.round(rgb[0])}, ${Math.round(rgb[1])}, ${Math.round(rgb[2])})` : '');
const compositedControlOverlayBackground = (backgroundRgb) => backgroundRgb?.map(
  (channel) => channel * (1 - CONTROL_OVERLAY_ALPHA),
) || null;

const isSelectedSurface = (element) => isElement(element) && Boolean(element.matches?.(SELECTED_SURFACE_SELECTOR));

const SELECTED_SURFACE_PROPERTIES = [
  '--og-selected-background',
  '--og-selected-surface',
  '--og-selected-foreground',
  '--og-text-on-selected',
  '--og-icon-on-selected',
];

// Selected-state tokens are transient output, not component configuration.
// Remove the previous snapshot before sampling a control that has transitioned
// back to its default state. Otherwise the contrast pass reads its own stale
// selected surface and permanently paints every previously selected control.
const reconcileSelectedSurfaceState = (element) => {
  if (!isElement(element) || isSelectedSurface(element)) return;
  const hadSelectedSnapshot = element.hasAttribute('data-og-selected-contrast-surface');
  const selectedSnapshotProperties = hadSelectedSnapshot ? [
    ...Object.keys(buildComponentSurfaceVariables('#0f172a', '#ffffff', { scope: element })),
    '--og-container-background', '--og-container-surface', '--og-container-foreground',
    '--og-container-secondary-foreground', '--og-container-icon', '--og-container-border',
    '--og-container-link', '--og-container-link-visited', '--og-container-focus',
    '--og-local-background', '--og-local-surface', '--og-local-foreground', '--og-local-icon',
    '--og-context-foreground', '--og-text-on-local-surface', '--og-icon-on-local-surface',
    '--og-adaptive-foreground', '--og-inline-editable-foreground',
    '--og-button-local-foreground', '--og-button-resolved-foreground',
    '--og-button-icon-foreground', '--og-button-text-adaptive', '--og-text-on-button',
    '--og-control-foreground',
  ] : [];
  [...new Set([...SELECTED_SURFACE_PROPERTIES, ...selectedSnapshotProperties])].forEach((property) => {
    if (element.style.getPropertyValue(property)) {
      visualAuthority.requestStyles('LocalSurfaceContrastEngine', element, { [property]: null });
    }
  });
  if (hadSelectedSnapshot) {
    visualAuthority.requestStyles('LocalSurfaceContrastEngine', element, {
      color: null,
      '-webkit-text-fill-color': null,
      'webkit-text-fill-color': null,
    });
  }
  element.removeAttribute('data-og-selected-contrast-surface');
  element.removeAttribute('data-og-selected-contrast-fixed');
};

const isButtonLike = (element) => isElement(element) && Boolean(element.matches?.(
  ACCESSIBLE_CONTROL_SELECTOR
));

const hasLocalizedButtonField = (buttonElement) => (
  isButtonLike(buttonElement)
  && (
    buttonElement.matches?.(LOCALIZED_BUTTON_FIELD_SELECTOR)
    || Boolean(buttonElement.querySelector?.(LOCALIZED_BUTTON_FIELD_SELECTOR))
  )
);

const getInlineBackgroundRgb = (element) => {
  if (!isElement(element)) {
    return null;
  }
  const inlineBackground = element.style.getPropertyValue('background-color')
    || element.style.getPropertyValue('background')
    || '';
  const inlineColor = String(inlineBackground || '').match(/rgba?\([^)]+\)|#[0-9a-f]{3,8}\b/i)?.[0] || inlineBackground;
  return isTransparent(inlineColor) ? null : parseComputedColor(inlineColor);
};

const foregroundFor = (backgroundRgb) => {
  if (!backgroundRgb) {
    return '';
  }
  return resolveForegroundColor(rgbToCss(backgroundRgb));
};

const getControlBackground = (element) => {
  if (!isElement(element) || typeof window === 'undefined') {
    return { value: '', backgroundRgb: null, source: 'none' };
  }
  const computed = window.getComputedStyle(element);
  if (computed.backgroundImage && computed.backgroundImage !== 'none') {
    return {
      value: computed.backgroundImage,
      backgroundRgb: getElementBackgroundRgb(element),
      source: 'control-gradient',
    };
  }
  if (!isTransparent(computed.backgroundColor)) {
    const renderedBackground = getImmediateVisualBackground(element);
    return {
      value: renderedBackground || computed.backgroundColor,
      backgroundRgb: parseComputedColor(renderedBackground) || parseComputedColor(computed.backgroundColor),
      source: 'control-background',
    };
  }
  const renderedSurface = nearestLocalSurface(element.parentElement);
  return {
    value: rgbToCss(renderedSurface?.backgroundRgb),
    backgroundRgb: renderedSurface?.backgroundRgb || null,
    source: renderedSurface ? 'transparent-over-surface' : 'none',
  };
};

const getElementBackgroundRgb = (element) => {
  if (!isElement(element) || typeof window === 'undefined') {
    return null;
  }
  const inlineBackgroundRgb = getInlineBackgroundRgb(element);
  const computed = window.getComputedStyle(element);
  const hasGradient = computed.backgroundImage && computed.backgroundImage !== 'none';
  const hasOwnBackground = inlineBackgroundRgb
    || hasGradient
    || !isTransparent(computed.backgroundColor);
  if (!hasOwnBackground) {
    return null;
  }
  const renderedBackground = getImmediateVisualBackground(element);
  if (renderedBackground && parseComputedColor(renderedBackground)) {
    return parseComputedColor(renderedBackground);
  }
  if (inlineBackgroundRgb) return inlineBackgroundRgb;
  if (hasGradient) {
    const gradientColor = computed.backgroundImage.match(/rgba?\([^)]+\)|#[0-9a-f]{3,6}/i)?.[0];
    const gradientRgb = parseComputedColor(gradientColor || '');
    if (gradientRgb) {
      return gradientRgb;
    }
  }
  if (!isTransparent(computed.backgroundColor)) {
    return parseComputedColor(computed.backgroundColor);
  }
  return null;
};

const nearestLocalSurface = (element) => {
  let current = isElement(element) ? element : element?.parentElement;
  if (current?.hasAttribute?.('data-og-typography-role')) {
    current = current.parentElement;
  }
  while (current && current !== document.documentElement) {
    if (current.getAttribute?.('data-og-surface-boundary') === 'inherit') {
      current = current.parentElement;
      continue;
    }
    if (current.matches?.(SKIP_SELECTOR)) {
      return null;
    }
    const backgroundRgb = getElementBackgroundRgb(current);
    if (backgroundRgb) {
      return {
        element: current,
        backgroundRgb,
        preserveSurface: isEnterpriseWorkspaceStructure(current),
      };
    }
    current = current.parentElement;
  }
  return null;
};

const hasVisibleText = (element) => {
  if (!isElement(element)) {
    return false;
  }
  return Array.from(element.childNodes).some((node) => (
    node.nodeType === Node.TEXT_NODE && String(node.textContent || '').trim()
  ));
};

const shouldSkip = (element) => (
  !isElement(element)
  || isComponentOwnedControl(element)
  || isEnterpriseWorkspaceStructure(element)
  || (
    element.matches?.(SKIP_SELECTOR)
    && !(element.matches?.('[data-sidenav-link="true"]') && isButtonLike(element) && getElementBackgroundRgb(element))
  )
  || (
    element.closest?.(SKIP_SELECTOR)
    && !(
      element.closest?.('[data-sidenav-link="true"]')
      && getElementBackgroundRgb(element.closest('[data-sidenav-link="true"]'))
    )
  )
  || (
    element.closest?.('.og-orders-page, .og-template-orders-page')
    && element.matches?.('.storefront-inline-editable-value[data-og-adaptive-surface="true"], [data-og-inline-editable-value="true"][data-og-adaptive-surface="true"]')
  )
);

const setLocalSurfaceVariables = (element, backgroundRgb) => {
  const foreground = foregroundFor(backgroundRgb);
  const background = rgbToCss(backgroundRgb);
  if (!foreground || !background) {
    return '';
  }
  const inlineBackground = element.style.getPropertyValue('background-color')
    || element.style.getPropertyValue('background')
    || '';
  if (inlineBackground) {
    element.dataset.ogConfiguredBackground = inlineBackground;
  }
  // This diagnostic attribute was interpreted by downstream styling and
  // leaked a sampled foreground into unrelated descendants. Foreground
  // authority is represented by semantic variables only.
  element.removeAttribute('data-og-configured-foreground');
  const componentVars = buildComponentSurfaceVariables(background, foreground, { scope: element });
  // Background selection belongs to the page/component stylesheet. Publishing
  // the sampled background back as an inline !important custom property makes
  // every descendant that consumes --og-surface-background inherit a frozen
  // color. That breaks striped rows, alternating sections, state surfaces, and
  // any background that changes after the first contrast pass. The contrast
  // engine may derive foreground/state tokens from the sampled color, but must
  // never become the authority for the base surface background itself.
  delete componentVars['--og-surface-background'];
  const vars = {
    ...componentVars,
    '--og-container-background': background,
    '--og-container-surface': background,
    '--og-container-foreground': foreground,
    '--og-container-secondary-foreground': componentVars['--og-typography-medium'] || foreground,
    '--og-container-icon': foreground,
    '--og-container-border': foreground,
    '--og-container-link': foreground,
    '--og-container-link-visited': foreground,
    '--og-container-focus': foreground,
    '--og-local-background': background,
    '--og-local-surface': background,
    '--og-local-foreground': foreground,
    '--og-local-icon': foreground,
    '--og-context-foreground': foreground,
    '--og-text-on-local-surface': foreground,
    '--og-icon-on-local-surface': foreground,
    '--og-adaptive-foreground': foreground,
    '--og-inline-editable-foreground': foreground,
  };
  if (isSelectedSurface(element)) {
    Object.assign(vars, {
      '--og-selected-background': background,
      '--og-selected-surface': background,
      '--og-selected-foreground': foreground,
      '--og-text-on-selected': foreground,
      '--og-icon-on-selected': foreground,
    });
    element.setAttribute('data-og-selected-contrast-surface', 'true');
  }
  if (hasLocalizedButtonField(element)) {
    element.setAttribute('data-og-local-contrast-surface', 'true');
  }
  element.setAttribute('data-og-contrast-container', 'true');
  element.setAttribute('data-og-component-contract', 'compliant');
  element.setAttribute('data-og-style-authority', 'semantic-surface');
  element.setAttribute('data-og-style-lifecycle-stage', 'computed-semantic-tokens');
  const parentStyle = element.parentElement ? window.getComputedStyle(element.parentElement) : null;
  Object.entries(vars).forEach(([key, value]) => {
    // Custom properties inherit. A nested semantic boundary only needs an
    // inline value when it differs from its parent surface. Removing a stale
    // local declaration lets subsequent theme transactions propagate from the
    // nearest real surface boundary without walking every descendant.
    if (parentStyle?.getPropertyValue(key).trim() === String(value)) {
      if (element.style.getPropertyValue(key)) visualAuthority.requestStyles('LocalSurfaceContrastEngine', element, { [key]: null });
    } else if (element.style.getPropertyValue(key) !== value) {
      visualAuthority.requestStyles('LocalSurfaceContrastEngine', element, { [key]: value }, 'important');
    }
  });
  element.setAttribute('data-og-contrast-ratio', String(
    Math.min(contrastRatio(foreground, background), 21).toFixed(2),
  ));
  return foreground;
};

const applyForeground = (element, foreground, important = true) => {
  if (!foreground || !isElement(element)) {
    return;
  }
  if (element.style.getPropertyValue('color') !== foreground) {
    visualAuthority.requestStyles('LocalSurfaceContrastEngine', element, { color: foreground }, important ? 'important' : '');
  }
  if (element.style.getPropertyValue('-webkit-text-fill-color') !== foreground) {
    visualAuthority.requestStyles('LocalSurfaceContrastEngine', element, { '-webkit-text-fill-color': foreground }, important ? 'important' : '');
  }
  if (element.style.getPropertyValue('webkit-text-fill-color') !== foreground) {
    visualAuthority.requestStyles('LocalSurfaceContrastEngine', element, { 'webkit-text-fill-color': foreground }, important ? 'important' : '');
  }
};

export const enforceAccessibleControlForeground = (buttonElement) => {
  if (!isButtonLike(buttonElement) || shouldSkip(buttonElement)) {
    return;
  }
  const controlBackground = getControlBackground(buttonElement);
  if (!controlBackground.value || !controlBackground.backgroundRgb) {
    return;
  }
  let foreground = getAccessibleForeground(controlBackground.value, '', {
    minimumRatio: MIN_TEXT_CONTRAST,
    scope: buttonElement,
  });
  if (!foreground) {
    return;
  }
  const backgroundSamples = extractBackgroundColors(controlBackground.value, { scope: buttonElement });
  let minimumContrast = backgroundSamples.length
    ? Math.min(...backgroundSamples.map((sample) => contrastRatio(foreground, sample)))
    : contrastRatio(foreground, rgbToCss(controlBackground.backgroundRgb));
  const needsGradientContrastOverlay = (
    controlBackground.source === 'control-gradient'
    && minimumContrast < MIN_TEXT_CONTRAST
  );
  if (needsGradientContrastOverlay) {
    // A single foreground cannot satisfy AA over a gradient containing both very
    // light and very dark stops. The shared overlay makes the control's rendered
    // background uniformly dark enough for white text without changing its API.
    foreground = '#ffffff';
    minimumContrast = MIN_TEXT_CONTRAST;
    buttonElement.setAttribute('data-og-control-contrast-overlay', 'dark');
  } else {
    buttonElement.removeAttribute('data-og-control-contrast-overlay');
  }
  const renderedControlBackground = needsGradientContrastOverlay
    ? compositedControlOverlayBackground(controlBackground.backgroundRgb)
    : controlBackground.backgroundRgb;
  // The composited overlay is the rendered container. Feed it back through the
  // same semantic surface resolver so controls never have a second foreground
  // authority that disagrees with their tokens.
  foreground = setLocalSurfaceVariables(buttonElement, renderedControlBackground) || foreground;
  buttonElement.setAttribute('data-og-local-contrast-surface', 'true');
  buttonElement.setAttribute('data-og-button-local-contrast', 'true');
  buttonElement.setAttribute('data-og-control-background-source', controlBackground.source);
  buttonElement.setAttribute('data-og-control-contrast-ratio', String(
    Math.min(minimumContrast, 21).toFixed(2),
  ));
  applyForeground(buttonElement, foreground);
  const setSemanticProperty = (element, property, value, priority = 'important') => {
    if (element.style.getPropertyValue(property) === String(value)
      && element.style.getPropertyPriority(property) === priority) return;
    visualAuthority.requestStyles('LocalSurfaceContrastEngine', element, { [property]: value }, priority);
  };
  setSemanticProperty(buttonElement, '--og-button-local-foreground', foreground);
  setSemanticProperty(buttonElement, '--og-button-resolved-foreground', foreground);
  setSemanticProperty(buttonElement, '--og-button-icon-foreground', foreground);
  setSemanticProperty(buttonElement, '--og-button-text-adaptive', foreground);
  setSemanticProperty(buttonElement, '--og-text-on-button', foreground);
  setSemanticProperty(buttonElement, '--og-control-foreground', foreground);
  buttonElement.querySelectorAll?.(BUTTON_TEXT_DESCENDANT_SELECTOR).forEach((child) => {
    if (!isElement(child) || child.matches?.('[data-og-foreground-skip="true"], [data-og-preserve-colors="true"]')) {
      return;
    }
    applyForeground(child, foreground);
    if (child.matches?.(ICON_SELECTOR)) {
      visualAuthority.requestStyles('LocalSurfaceContrastEngine', child, { fill: 'currentColor', stroke: 'currentColor' }, 'important');
      child.setAttribute('data-og-theme-aware-icon', 'true');
    }
  });
};

const enforceElementContrast = (element, surface, minimumRatio = MIN_TEXT_CONTRAST) => {
  if (shouldSkip(element) || !surface?.backgroundRgb) {
    return;
  }
  const computed = window.getComputedStyle(element);
  const currentColor = computed.color;
  const background = rgbToCss(surface.backgroundRgb);
  const currentRgb = parseComputedColor(currentColor);
  if (!currentRgb || !background) {
    return;
  }
  const currentRatio = contrastRatio(currentColor, background);
  const readable = foregroundFor(surface.backgroundRgb);
  const typographyRole = element.getAttribute?.('data-og-typography-role') || '';
  const roleForeground = ['caption', 'hint', 'helper', 'metadata'].includes(typographyRole)
    ? deriveTypographyForeground(readable, background, SEMANTIC_TYPOGRAPHY_LOW_MIX, minimumRatio)
    : ['description', 'overline', 'breadcrumb'].includes(typographyRole)
      ? deriveTypographyForeground(readable, background, SEMANTIC_TYPOGRAPHY_MEDIUM_MIX, minimumRatio)
      : readable;
  const localSurfaceMustWin = !surface.preserveSurface && (
    element === surface.element
    || surface.element.matches?.(SURFACE_SELECTOR)
    || surface.element.getAttribute?.('data-og-style-authority') === 'semantic-surface'
  );
  const selectedSurfaceMustWin = isSelectedSurface(surface.element) || isSelectedSurface(element);
  if (selectedSurfaceMustWin || localSurfaceMustWin || currentRatio < minimumRatio) {
    if (!surface.preserveSurface) {
      setLocalSurfaceVariables(surface.element, surface.backgroundRgb);
      surface.element.setAttribute('data-og-local-contrast-surface', 'true');
    }
    if (typographyRole) {
      // The semantic typography stylesheet remains the final rendering
      // authority. This engine publishes surface tokens but never replaces a
      // semantic role with literal inline color or text-fill declarations.
      visualAuthority.requestStyles('LocalSurfaceContrastEngine', element, {
        color: null,
        '-webkit-text-fill-color': null,
        'webkit-text-fill-color': null,
        opacity: null,
      });
      element.removeAttribute('data-og-local-contrast-fixed');
      element.removeAttribute('data-og-local-contrast-surface');
      element.removeAttribute('data-og-contrast-container');
      element.removeAttribute('data-og-component-contract');
      element.setAttribute('data-og-style-authority', 'semantic-typography');
      element.setAttribute('data-og-style-lifecycle-stage', 'semantic-token-consumer');
      element.setAttribute('data-og-local-surface-foreground', roleForeground);
      return;
    } else {
      applyForeground(element, roleForeground);
    }
    element.setAttribute('data-og-local-contrast-fixed', 'true');
    element.setAttribute('data-og-local-surface-foreground', roleForeground);
    if (selectedSurfaceMustWin) {
      element.setAttribute('data-og-selected-contrast-fixed', 'true');
    }
  }
};

export const enforceLocalSurfaceContrast = (root = document.body) => {
  if (typeof window === 'undefined' || !root) {
    return;
  }

  const surfaces = new Set([
    ...(root.matches?.(SURFACE_SELECTOR) ? [root] : []),
    ...Array.from(root.querySelectorAll?.(SURFACE_SELECTOR) || []),
    ...(root.matches?.(SELECTED_SURFACE_SELECTOR) ? [root] : []),
    ...Array.from(root.querySelectorAll?.(SELECTED_SURFACE_SELECTOR) || []),
  ]);

  surfaces.forEach((surfaceElement) => {
    if (
      shouldSkip(surfaceElement)
      || surfaceElement.hasAttribute?.('data-og-typography-role')
      || surfaceElement.getAttribute?.('data-og-surface-boundary') === 'inherit'
    ) {
      return;
    }
    reconcileSelectedSurfaceState(surfaceElement);
    let backgroundRgb = getElementBackgroundRgb(surfaceElement);
    if (!backgroundRgb) {
      const inheritedSurface = nearestLocalSurface(surfaceElement.parentElement);
      backgroundRgb = inheritedSurface?.backgroundRgb || null;
    }
    if (!backgroundRgb) {
      return;
    }
    setLocalSurfaceVariables(surfaceElement, backgroundRgb);
    if (hasVisibleText(surfaceElement) || surfaceElement.matches('button, .btn, .MuiButton-root, .MuiChip-root, .badge, .MuiTab-root, .MuiMenuItem-root, .MuiListItemButton-root, .MuiTableCell-root, .MuiAccordionSummary-root') || isSelectedSurface(surfaceElement)) {
      enforceElementContrast(surfaceElement, { element: surfaceElement, backgroundRgb });
    }
  });

  // Resolve descendants once. Scanning every surface's complete subtree made
  // nested workspaces O(surfaces × descendants) and monopolized the main
  // thread immediately after first paint.
  const icons = [
    ...(root.matches?.(ICON_SELECTOR) ? [root] : []),
    ...Array.from(root.querySelectorAll?.(ICON_SELECTOR) || []),
  ];
  icons.forEach((iconElement) => {
    const local = nearestLocalSurface(iconElement);
    if (!local) return;
    enforceElementContrast(iconElement, local, MIN_ICON_CONTRAST);
    visualAuthority.requestStyles('LocalSurfaceContrastEngine', iconElement, { fill: 'currentColor', stroke: 'currentColor' }, 'important');
  });

  const buttons = new Set([
    ...(root.matches?.(ACCESSIBLE_CONTROL_SELECTOR) ? [root] : []),
    ...Array.from(root.querySelectorAll?.(ACCESSIBLE_CONTROL_SELECTOR) || []),
  ]);
  buttons.forEach((buttonElement) => {
    enforceAccessibleControlForeground(buttonElement);
  });

  // Mutation reconciliation is intentionally scoped to each added node. The
  // scope itself must participate; querySelectorAll only returns descendants
  // and previously left lazy-rendered headings/metadata permanently unaudited.
  const looseText = [
    ...(root.matches?.(TEXT_SELECTOR) ? [root] : []),
    ...Array.from(root.querySelectorAll?.(TEXT_SELECTOR) || []),
  ];
  looseText.forEach((textElement) => {
    if (!hasVisibleText(textElement) && !textElement.matches('button, .MuiButton-root, a')) {
      return;
    }
    const local = nearestLocalSurface(textElement);
    if (local) {
      enforceElementContrast(textElement, local);
    }
  });
};

const schedule = (callback, frameRef) => {
  if (typeof window === 'undefined') {
    return;
  }
  window.cancelAnimationFrame(frameRef.current);
  frameRef.current = window.requestAnimationFrame(callback);
};

export function LocalSurfaceContrastProvider({ children }) {
  const rootRef = useRef(null);
  const frameRef = useRef(0);
  const settleTimerRef = useRef([]);
  const pendingRootsRef = useRef(new Set());

  useLayoutEffect(() => {
    if (typeof window === 'undefined' || !rootRef.current) {
      return undefined;
    }
    const rootElement = rootRef.current;
    const run = (scope = rootElement) => enforceLocalSurfaceContrast(scope);
    const scheduleScope = (scope = rootElement) => {
      const candidate = scope?.nodeType === Node.ELEMENT_NODE ? scope : scope?.parentElement;
      if (!candidate || !rootElement.contains(candidate)) return;
      pendingRootsRef.current.add(candidate);
      schedule(() => {
        const pending = Array.from(pendingRootsRef.current);
        pendingRootsRef.current.clear();
        const roots = pending.filter((entry) => !pending.some((other) => other !== entry && other.contains(entry)));
        roots.forEach((entry) => run(entry));
      }, frameRef);
    };
    const reconcileLifecycleChange = () => {
      scheduleScope(rootElement);
      settleTimerRef.current.forEach((timer) => window.clearTimeout(timer));
      // One bounded settlement handles consumers committed after the theme
      // event without repeatedly rescanning the complete application tree.
      settleTimerRef.current = [120].map((delay) => window.setTimeout(() => scheduleScope(rootElement), delay));
    };
    // The initial reconciliation is synchronous so configured surfaces never
    // paint with an ambient/page foreground before semantic tokens are ready.
    run();
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length) {
          mutation.addedNodes.forEach((node) => scheduleScope(node));
        } else {
          scheduleScope(mutation.target);
        }
      });
    });
    observer.observe(rootElement, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: [
        'class',
        'style',
        'aria-current',
        'aria-pressed',
        'aria-selected',
        'data-og-adaptive-surface',
        'data-og-context-foreground',
        'data-og-selected',
        'data-state',
      ],
    });
    const reconcileRoot = () => scheduleScope(rootElement);
    window.addEventListener('resize', reconcileRoot);
    // Theme publication is complete and immutable. This consumer must not run
    // a second visual pass in response to that publication.
    window.addEventListener('opengrit:page-style-updated', reconcileLifecycleChange);
    // Hover, focus, pressed, transitions, and animations are transient visual
    // states. Sampling them and publishing the result as inline semantic tokens
    // turns a momentary CSS state into persistent component state. That feedback
    // loop made control foreground/background values alternate across clicks.
    // Persistent state changes are already covered by the MutationObserver
    // (class, aria-*, and data-state); input/change do not alter a control surface.
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const scheduledFrame = frameRef.current;
      observer.disconnect();
      window.removeEventListener('resize', reconcileRoot);
      window.removeEventListener('opengrit:page-style-updated', reconcileLifecycleChange);
      window.cancelAnimationFrame(scheduledFrame);
      settleTimerRef.current.forEach((timer) => window.clearTimeout(timer));
    };
  }, []);

  return (
    <div ref={rootRef} data-og-local-surface-contrast-root="true">
      {children}
    </div>
  );
}

// Permanent design-system name. The legacy export remains for downstream
// packages while all observation and reconciliation is owned by this provider.
export const ContainerContrastProvider = LocalSurfaceContrastProvider;

LocalSurfaceContrastProvider.propTypes = {
  children: PropTypes.node,
};

LocalSurfaceContrastProvider.defaultProps = {
  children: null,
};

export const __localSurfaceContrastTestUtils = {
  compositedControlOverlayBackground,
  foregroundFor,
  getElementBackgroundRgb,
  isSelectedSurface,
  nearestLocalSurface,
};
